// assets
import ApartmentOutlined from '@ant-design/icons/ApartmentOutlined';
import AppstoreOutlined from '@ant-design/icons/AppstoreOutlined';
import BankOutlined from '@ant-design/icons/BankOutlined';
import BookOutlined from '@ant-design/icons/BookOutlined';
import CalendarOutlined from '@ant-design/icons/CalendarOutlined';
import FileOutlined from '@ant-design/icons/FileOutlined';
import FormOutlined from '@ant-design/icons/FormOutlined';
import LineChartOutlined from '@ant-design/icons/LineChartOutlined';
import MessageOutlined from '@ant-design/icons/MessageOutlined';
import PictureOutlined from '@ant-design/icons/PictureOutlined';
import SafetyCertificateOutlined from '@ant-design/icons/SafetyCertificateOutlined';
import SettingOutlined from '@ant-design/icons/SettingOutlined';
import StarOutlined from '@ant-design/icons/StarOutlined';
import TableOutlined from '@ant-design/icons/TableOutlined';
import TeamOutlined from '@ant-design/icons/TeamOutlined';
import UserOutlined from '@ant-design/icons/UserOutlined';

// project imports
import { buildVisibilityFilter } from './catalogueVisibility';

// ==============================|| MENU ITEMS - ADMIN LEVELS (L0 / L1 / L2) ||============================== //

/**
 * The three admin levels from the Admin Persona mockup, keyed by the role code
 * the backend actually returns in the login response (`user.role`).
 *
 *   L0 ayro_it_admin — Ayro IT / System Administrator. The full surface.
 *   L1 ayro_admin    — Ayro Administrator. Same reach, but the platform's own
 *                      shape and packaging are not his to change.
 *   L2 org_admin     — Organization Administrator. One organization.
 */
export const ADMIN_LEVELS = {
  IT: 'ayro_it_admin',
  GLOBAL: 'ayro_admin',
  ORG: 'org_admin',
  // L3 center_admin — Clinical Center Admin. Same admin experience as an org admin, but
  // scoped to the single centre they are assigned to (data scoping is the API's job). It
  // routes as an admin via menu_template = 'org_admin', so the /admin/* RoleGuards — which
  // already admit org_admin — admit it too; this only gives it its own left menu.
  CENTER: 'center_admin'
};

const { IT, GLOBAL, ORG, CENTER } = ADMIN_LEVELS;

const ALL = [IT, GLOBAL, ORG, CENTER];
// Platform configuration: what the platform IS and how it is packaged. An
// organization administrator never sees these — they are not scoped to an
// organization, so there is no meaningful "their own" version of them.
const PLATFORM_ONLY = [IT, GLOBAL];
// Developer scaffolding, not part of the product surface.
const IT_ONLY = [IT];

/**
 * One definition, filtered per level — rather than three hand-maintained menus
 * that drift apart the first time someone adds a screen.
 *
 * `levels`   who may see the item.
 * `pending`  the screen is not built yet. The item stays fully enabled and
 *            routes to /admin/under-development/<id>, which explains what is
 *            coming. Greying these out was worse: with most of the menu
 *            disabled the app read as broken rather than in progress, and a
 *            dead control gives no explanation for why it will not respond.
 *            Give the item a real `url` and drop `pending` as each screen lands.
 * `desc`     shown on the under-development page. One line on what the screen
 *            will do, so the placeholder is informative rather than a dead end.
 * `titleFor` per-level label. The mockup relabels the hierarchy screen to
 *            "Manage Centers" for an organization admin, whose tree is rooted
 *            at their own organization.
 */
const SECTIONS = [
  {
    id: 'group-admin-clinical',
    title: 'Clinical',
    items: [
      {
        id: 'dashboard-reports',
        title: 'Dashboard & Reports',
        url: '/admin/dashboard',
        icon: LineChartOutlined,
        levels: ALL,
        feature: 'dashboard_reports',
        catalogueCode: 'dashboard_reports'
      },
      // These three already exist — they were built for the supervisor and are
      // reached at their existing routes rather than rebuilt under /admin. An
      // administrator sees the same screen; what differs is the scope of the
      // data in it, which is the API's job, not the route's. They may well be
      // empty, and empty is the correct thing to show — a "coming soon" page
      // for a screen that exists would simply be wrong.
      {
        id: 'calendar',
        title: 'Calendar',
        url: '/supervisor/calendar',
        icon: CalendarOutlined,
        levels: ALL,
        feature: 'calendar',
        catalogueCode: 'calendar'
      },
      {
        id: 'child-profiles',
        title: 'Child Profiles',
        url: '/supervisor/child',
        icon: UserOutlined,
        levels: ALL,
        feature: 'child_profiles',
        catalogueCode: 'child_profiles'
      },
      {
        id: 'therapist-profiles',
        title: 'Therapist Profiles',
        url: '/supervisor/therapists',
        icon: TeamOutlined,
        levels: ALL,
        feature: 'therapist_profiles',
        catalogueCode: 'therapist_profiles'
      },
      {
        // Sits beside Therapist Profiles because it answers the same question
        // about the other half of the clinical staff. catalogueCode must exist
        // as an rbac_app_page row or the entry resolves to nothing - see
        // scripts/dml/seed for how the catalogue is promoted.
        id: 'supervisor-profiles',
        title: 'adminProductTiers.item.supervisor_profiles',
        url: '/supervisor/supervisors',
        icon: TeamOutlined,
        levels: ALL,
        feature: 'supervisor_profiles',
        catalogueCode: 'supervisor_profiles'
      },
      {
        id: 'skills',
        title: 'Skill Repository',
        url: '/admin/skills',
        icon: BookOutlined,
        levels: ALL,
        feature: 'skill_repository',
        catalogueCode: 'skill_repository'
      },
      {
        id: 'materials',
        title: 'Material Repository',
        url: '/admin/materials',
        icon: PictureOutlined,
        levels: ALL,
        feature: 'material_repository',
        catalogueCode: 'material_repository'
      },
      // `catalogueCode` is the Features and Pages switch (rbac_app_feature.code);
      // `feature` is the product-tier entitlement code. They are separate code
      // spaces and must not be merged — the two builder features below have a tier
      // code of `form_customization` / `workflow_customization` against catalogue
      // codes of `forms_library` / `workflow`. Every routed item carries its
      // `catalogueCode` so the Features and Pages switch is matched by CODE, not by
      // URL: the catalogue stores one route per feature and the same screen is
      // reached at different role-prefixed URLs, so a URL join is unreliable — it is
      // what left switched-off items still showing here.
      {
        id: 'communication-diary',
        feature: 'communication_diary',
        catalogueCode: 'communication_diary',
        title: 'Communication Diary',
        icon: MessageOutlined,
        levels: ALL,
        pending: true,
        desc: 'Messages between the care team and caregivers, per child.'
      },
      {
        id: 'activity-center',
        feature: 'activity_center',
        catalogueCode: 'activity_center',
        title: 'Activity Center',
        icon: AppstoreOutlined,
        levels: ALL,
        pending: true,
        desc: 'Activities and home practice assigned to children in your scope.'
      },
      // Clinical Settings — the same screen the supervisor reaches at
      // /supervisor/settings, whose Mastery Criteria / Datasheet Mapping / Data
      // Collection Settings / Reference Data pages are its TABS (gated in-context
      // by their page grants, exactly as on the supervisor screen). Not a
      // platform-admin default (`levels: []`), so it appears ONLY when the role
      // is granted `clinical_settings` in Role Definitions — additive on grants
      // like the persona menus — and disappears when it is un-ticked (denied).
      // Uses the supervisor route because the admin menu already reaches the
      // shared clinical screens at their /supervisor/* URLs (Calendar, Child
      // Profiles, Therapist Profiles).
      {
        id: 'clinical-settings',
        catalogueCode: 'clinical_settings',
        title: 'Settings',
        url: '/supervisor/settings',
        icon: SettingOutlined,
        levels: []
      }
    ]
  },
  {
    id: 'group-admin-administration',
    title: 'Administration',
    items: [
      {
        id: 'user-management',
        title: 'User Management',
        url: '/admin/user-management',
        icon: TeamOutlined,
        levels: ALL,
        catalogueCode: 'user_management'
      },
      {
        id: 'organizations-centers',
        title: 'Organizations and Centers',
        titleFor: { [ORG]: 'Manage Centers', [CENTER]: 'Manage Centers' },
        url: '/admin/organizations-centers',
        icon: BankOutlined,
        levels: ALL,
        catalogueCode: 'organizations_centers'
      },
      {
        id: 'role-definitions',
        title: 'Role Definitions',
        url: '/admin/role-definitions',
        icon: SafetyCertificateOutlined,
        levels: ALL,
        catalogueCode: 'role_definitions'
      },
      {
        id: 'access-matrix',
        title: 'Access Matrix',
        url: '/admin/access-matrix',
        icon: TableOutlined,
        levels: ALL,
        catalogueCode: 'access_matrix'
      },

      // --- platform configuration: L0 and L1 only -------------------------
      {
        id: 'features-pages',
        title: 'Features and Components',
        url: '/admin/features-pages',
        icon: FileOutlined,
        levels: PLATFORM_ONLY,
        catalogueCode: 'features_pages'
      },
      {
        // All three admin personas may open this; the page is read-only for
        // everyone but Ayro IT admin. Org admins get a read-only view (product
        // requirement), so this is ALL rather than PLATFORM_ONLY.
        id: 'product-tiers',
        catalogueCode: 'product_tiers',
        title: 'Product Tiers',
        url: '/admin/product-tiers',
        icon: StarOutlined,
        levels: ALL
      },
      // GRANT-DRIVEN (`levels: []`): Forms Library and Workflow appear ONLY when the
      // role is granted the feature in Role Definitions — for every admin level. A
      // level floor (previously PLATFORM_ONLY) made them show for IT/Ayro Admin
      // regardless of the grant, so ENABLING was a no-op (already visible) and only an
      // explicit disable removed them — the "toggling Forms/Workflow does nothing" bug.
      // Making them pure grant-driven (like Settings) means enable shows and disable
      // hides, symmetrically, for all three platform roles. The platform roles grant
      // these by default in their permission rows, so their default sidebar is
      // unchanged; org/centre admins (which don't grant them) still don't see them.
      // Still gated by the organization's product tier through `feature`.
      {
        id: 'form-builder',
        feature: 'form_customization',
        catalogueCode: 'forms_library',
        title: 'Forms Library',
        url: '/admin/form-builder',
        icon: FormOutlined,
        levels: [],
        desktopOnly: true
      },
      {
        id: 'workflow-builder',
        feature: 'workflow_customization',
        catalogueCode: 'workflow',
        title: 'Workflow',
        url: '/admin/workflow-builder',
        icon: ApartmentOutlined,
        levels: [],
        desktopOnly: true
      },
      {
        id: 'settings',
        catalogueCode: 'system_settings',
        title: 'System Settings',
        icon: SettingOutlined,
        levels: PLATFORM_ONLY,
        pending: true,
        desc: 'Platform-wide settings: region and language, timezone, Ayro branding, and data retention.'
      },

      // --- developer scaffolding: L0 only ---------------------------------
      // Hidden for now at Niteesh's request — these are template scaffolding
      // rather than product surface, and are not being worked on. The routes
      // still exist and still resolve, so removing `hidden` brings them back
      // without touching anything else.
      {
        id: 'components',
        title: 'Components',
        url: '/admin/components',
        icon: AppstoreOutlined,
        levels: IT_ONLY,
        hidden: true,
        catalogueCode: 'components'
      },
      {
        id: 'pages',
        title: 'Pages',
        icon: FileOutlined,
        levels: IT_ONLY,
        hidden: true,
        children: [
          { id: 'pages-select-center', title: 'Select Center', url: '/admin/pages/select-center' },
          { id: 'pages-select-program', title: 'Select Program', url: '/admin/pages/select-program' },
          { id: 'pages-select-grade', title: 'Select Grade', url: '/admin/pages/select-grade' },
          { id: 'pages-coming-soon', title: 'Coming Soon', url: '/admin/pages/coming-soon' }
        ]
      }
    ]
  }
];

// URL ↔ catalogueCode for the routed admin items, so CatalogueGuard can block a
// switched-off feature by CODE at its real URL — the same code-based join the sidebar
// uses — rather than depending on the catalogue's stored route lining up. Pending
// items (no real url) are skipped.
export const ADMIN_CATALOGUE_ROUTES = SECTIONS.flatMap((section) =>
  section.items
    .filter((item) => item.url && item.catalogueCode && !item.pending)
    .map((item) => ({ url: item.url, catalogueCode: item.catalogueCode }))
);

// Route for a screen that has not been built yet. Each pending item gets its
// OWN url rather than all of them sharing one, so the sidebar still highlights
// the item you actually clicked.
export const UNDER_DEVELOPMENT_BASE = '/admin/under-development';
const pendingUrl = (id) => `${UNDER_DEVELOPMENT_BASE}/${id}`;

const toMenuItem = (item, level) => {
  const node = {
    id: `admin-${item.id}`,
    title: (item.titleFor && item.titleFor[level]) || item.title,
    icon: item.icon,
    breadcrumbs: false
  };
  if (item.children) {
    node.type = 'collapse';
    node.children = item.children.map((child) => ({
      id: `admin-${child.id}`,
      title: child.title,
      type: 'item',
      url: child.url,
      breadcrumbs: false
    }));
    return node;
  }
  node.type = 'item';
  node.url = item.pending ? pendingUrl(item.id) : item.url;
  if (item.desktopOnly) node.desktopOnly = true;
  return node;
};

/**
 * Look up a pending feature by the id in the under-development route, so the
 * placeholder page can name the screen the user actually clicked instead of
 * showing one generic message for all of them.
 *
 * `level` is optional and only affects the label — an organization admin who
 * clicked "Manage Centers" should not land on a page headed "Organizations and
 * Centers".
 */
export function getPendingFeature(id, level) {
  for (const section of SECTIONS) {
    const item = section.items.find((i) => i.id === id && i.pending);
    if (item) {
      return {
        title: (item.titleFor && item.titleFor[level]) || item.title,
        description: item.desc,
        section: section.title
      };
    }
  }
  return null;
}

/**
 * Build the left menu for one admin level.
 *
 * Returns null for a role that is not one of the three admin levels, so the
 * caller can fall through to its existing per-role menus rather than having
 * this module guess.
 */
export function getAdminMenuForLevel(
  level,
  entitledFeatures,
  visibility = null,
  deniedPages = null,
  grantedCodes = null,
  deniedFeatures = null
) {
  if (!Object.values(ADMIN_LEVELS).includes(level)) return null;

  // The Features and Pages switch. Separate gate from tier entitlement below:
  // tier says what the organization bought, this says whether the capability is
  // switched on at all. Null means not yet known and hides nothing — see
  // buildVisibilityFilter. Protected codes never appear as disabled, so an admin
  // cannot switch off the screen that switches things back on.
  const switchedOff = buildVisibilityFilter(visibility);
  const switchedOn = (item) => !switchedOff || !switchedOff.isHidden(item);

  // `entitledFeatures` is the organization's product-tier entitlement, from
  // GET /organizations/my-features. Null means NOT YET KNOWN (request in flight,
  // or it failed) — in that case nothing is filtered, because blanking the menu
  // on a pending lookup, or on a network blip, is far worse than briefly showing
  // an item the tier does not cover. An item with no `feature` is never gated:
  // only capabilities Product Tiers actually packages carry a code.
  const gated = Array.isArray(entitledFeatures) ? new Set(entitledFeatures) : null;
  const entitled = (item) => !gated || !item.feature || gated.has(item.feature);

  // The PER-ROLE narrowing from Role Definitions (`denied_pages` on GET
  // /catalogue/my-menu) — the pages an admin explicitly un-ticked for this role.
  //
  // The three levels above say what a role MAY see; this says what was taken away, and
  // it has to agree with CatalogueGuard or the sidebar advertises a route the guard
  // bounces. That is not hypothetical: /admin/access-matrix sat in the sidebar while
  // every click redirected silently to the dashboard.
  //
  // Matched by URL, not by code, because `denied_pages` carries PAGE codes
  // (`access_matrix_page`) while these items carry FEATURE codes (`access_matrix`) —
  // and because the guard matches by URL, so doing the same here makes the two agree by
  // construction. `item.url` is the real route: a `pending` item has none, so a
  // placeholder is never hidden by a denial.
  //
  // Nothing is exempted. The backend decides what counts as denied; mirroring it
  // exactly is the whole point, and a menu that disagrees "to be safe" is the bug.
  const deniedFilter = deniedPages?.length ? buildVisibilityFilter({ disabled_features: [], disabled_pages: deniedPages }) : null;
  const notDenied = (item) => !deniedFilter || !item.url || !deniedFilter.urlHidden(item.url);

  // The PER-ROLE FEATURE-level narrowing from Role Definitions (`denied_features`).
  // denied_pages above is matched by URL and so cannot reach two kinds of item this
  // menu has: the routeless "pending" screens (Communication Diary, Activity Center,
  // System Settings), and any item whose feature was un-ticked as a whole rather than
  // page by page — which is exactly how the platform roles express their restrictions.
  // Matched by CODE against each item's catalogueCode (a FEATURE code here), so a
  // feature an admin un-ticked disappears, placeholder or not. Only EXPLICIT denials
  // are listed (never features_pages / role_definitions), so this only ever hides an
  // item someone deliberately turned off — it never subtracts by absence of a grant.
  const featureDenied = Array.isArray(deniedFeatures) ? new Set(deniedFeatures.map((d) => (typeof d === 'string' ? d : d.code))) : null;
  const notFeatureDenied = (item) => !featureDenied || !item.catalogueCode || !featureDenied.has(item.catalogueCode);

  // GRANT-DRIVEN with a level FALLBACK. Role Definitions is the source of truth: a coded
  // item that has an explicit permission row for this role is decided by that row —
  // GRANTED shows it, DENIED hides it — so both enabling AND disabling a feature reflect
  // in the sidebar. Only when there is NO row (or the grant lookup has not landed yet)
  // does the `levels` floor decide.
  //
  // Why not just OR the level floor with the grant (the previous rule)? A level-defaulted
  // item (Child Profiles = ALL, Forms Library = PLATFORM_ONLY, …) then showed purely from
  // its level regardless of the grant, so ENABLING it in Role Definitions was a no-op — it
  // was already visible — which is the "toggling does nothing" bug.
  //
  // Safe for every level: the PLATFORM roles carry an explicit grant/deny row for every
  // feature, so they are fully toggle-driven; org/centre admins with only partial rows
  // fall back to their curated level default instead of blanking, and the initial load
  // (grants unknown) shows the level default rather than an empty menu. Items with no
  // catalogueCode (developer scaffolding) are governed by their level alone.
  const granted = Array.isArray(grantedCodes) ? new Set(grantedCodes) : null;
  const permitted = (item) => {
    if (!item.catalogueCode) return item.levels.includes(level);
    if (granted && granted.has(item.catalogueCode)) return true;
    if (featureDenied && featureDenied.has(item.catalogueCode)) return false;
    return item.levels.includes(level);
  };

  // A `hidden` item (developer scaffolding — Components, the Pages group) stays out of
  // the menu UNLESS the role has been explicitly granted its catalogue feature. That lets
  // an admin surface e.g. Components by ticking it in Role Definitions, additively, while
  // keeping it off by default; un-ticking it drops it back out (also caught by the grant
  // check below and by notFeatureDenied). Items with no catalogueCode (the Pages group)
  // can never be granted, so they stay hidden.
  const notHidden = (item) => !item.hidden || (granted && item.catalogueCode ? granted.has(item.catalogueCode) : false);

  return SECTIONS.map((section) => ({
    id: section.id,
    title: section.title,
    type: 'group',
    children: section.items
      .filter(
        (item) => notHidden(item) && permitted(item) && entitled(item) && switchedOn(item) && notDenied(item) && notFeatureDenied(item)
      )
      .map((item) => toMenuItem(item, level))
  })).filter((section) => section.children.length > 0);
}

export default getAdminMenuForLevel;
